import { useState } from "react";
import { api } from "../../api/apiClient";
import type {
  CustomerOrder,
  RecordableOrderPaymentKind,
  StaffOrderPaymentMethod,
} from "../../api/types";
import { Modal } from "../Modal";

/** The order ledger's methods (it has no Online) — also the office ring-up's. */
export const ORDER_PAYMENT_METHODS: Array<{ value: StaffOrderPaymentMethod; label: string }> = [
  { value: "CASH", label: "Cash" },
  { value: "CARD", label: "Card" },
  { value: "BANK_TRANSFER", label: "Transfer" },
];

/**
 * OD-30: record the BUYER's money on an order. Couriers never take payment
 * (the recipient at the door is usually not the buyer), so a remainder owed
 * after delivery is settled here — the transfer that lands the next morning,
 * a phone card link, or a refund on a cancelled order.
 */
export function RecordPaymentModal({
  order,
  onClose,
  onSaved,
  onRefundGoods,
}: {
  order: CustomerOrder;
  onClose: () => void;
  onSaved: () => Promise<void>;
  /**
   * G-162 (owner 2026-10-19): on a handed-over order a refund here is only
   * for money beyond what was charged; goods go back through the refund
   * window. Present when that window can open.
   */
  onRefundGoods?: () => void;
}) {
  const due = order.balanceDueEur;
  const isCancelled = order.status === "CANCELLED";
  const [kind, setKind] = useState<RecordableOrderPaymentKind>(
    isCancelled ? "REFUND" : "BALANCE",
  );
  const [method, setMethod] = useState<StaffOrderPaymentMethod>("BANK_TRANSFER");
  const [amount, setAmount] = useState(
    !isCancelled && due && Number(due) > 0 ? due : "",
  );
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const save = async () => {
    const value = Number(amount);
    if (!Number.isFinite(value) || value <= 0) {
      setError("Enter the amount that changed hands.");
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await api.recordOrderPayment(order.id, {
        kind,
        method,
        amountEur: value,
        notes: notes.trim() || undefined,
      });
      await onSaved();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Could not save.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title={`Record payment · ${order.code}`} onClose={onClose}>
      <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-3)" }}>
        <p className="muted" style={{ margin: 0 }}>
          {order.totalEur
            ? `Agreed ${order.totalEur} €${
                // G-112 (OD-100): the fee is owed through this ledger too.
                Number(order.deliveryFeeEur) > 0
                  ? ` + ${order.deliveryFeeEur} € delivery`
                  : ""
              } · paid ${order.paidEur} €${
                due != null ? ` · outstanding ${due} €` : ""
              }`
            : "This order has no agreed price yet."}
        </p>

        <div className="field">
          <label htmlFor="rp-kind">What is this?</label>
          <div className="button-row" id="rp-kind">
            {(isCancelled
              ? (["REFUND"] as const)
              : (["BALANCE", "DEPOSIT", "REFUND"] as const)
            ).map((k) => (
              <button
                key={k}
                type="button"
                className={kind === k ? "button" : "button plain"}
                onClick={() => {
                  setKind(k);
                  // G-162: a refund here is the money beyond what is owed — offer it.
                  if (k === "REFUND" && order.overpaidEur && Number(order.overpaidEur) > 0) {
                    setAmount(order.overpaidEur);
                  }
                }}
                disabled={busy}
              >
                {k === "BALANCE"
                  ? "Payment"
                  : k === "DEPOSIT"
                    ? "Deposit"
                    : "Refund"}
              </button>
            ))}
          </div>
          {isCancelled ? (
            <small className="hint">
              A cancelled order can only receive a refund.
            </small>
          ) : null}
          {kind === "REFUND" && onRefundGoods ? (
            <div className="button-row">
              <span className="muted">Overpaid only · goods:</span>
              <button type="button" className="button plain" onClick={onRefundGoods} disabled={busy}>
                Refund goods
              </button>
            </div>
          ) : null}
        </div>

        <div className="field">
          <label htmlFor="rp-method">How</label>
          <div className="button-row" id="rp-method">
            {ORDER_PAYMENT_METHODS.map((m) => (
              <button
                key={m.value}
                type="button"
                className={method === m.value ? "button" : "button plain"}
                onClick={() => setMethod(m.value)}
                disabled={busy}
              >
                {m.label}
              </button>
            ))}
          </div>
        </div>

        <div className="field">
          <label htmlFor="rp-amount">Amount (€)</label>
          <input
            id="rp-amount"
            type="number"
            inputMode="decimal"
            min="0.01"
            step="0.01"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            disabled={busy}
          />
        </div>

        <div className="field">
          <label htmlFor="rp-notes">Note (optional)</label>
          <input
            id="rp-notes"
            type="text"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="e.g. transfer reference"
            disabled={busy}
          />
        </div>

        {error ? <div className="alert error">{error}</div> : null}

        <div className="button-row" style={{ justifyContent: "flex-end" }}>
          <button type="button" className="button plain" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button type="button" className="button" onClick={() => void save()} disabled={busy}>
            {busy ? "Saving…" : "Record"}
          </button>
        </div>
      </div>
    </Modal>
  );
}
